import { useEffect, useState, useRef, useCallback } from 'react';
import { useQuery } from 'convex/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import useAppStore from '../../stores/appStore';
import useFileStore from '../../stores/fileStore';
import useAIStore from '../../stores/aiStore';
import usePendingChangesStore from '../../stores/pendingChangesStore';
import FileTree from '../Sidebar/FileTree';
import EditorTabs from '../Editor/EditorTabs';
import RichTextEditor from '../Editor/RichTextEditor';
import CanvasEditor from '../Editor/CanvasEditor';
import DiffView from '../Editor/DiffView';
import AssistantPanel from '../AI/AssistantPanel';
import KeyboardShortcuts from '../Editor/KeyboardShortcuts';
import { isCanvasFile } from '../../utils/fileSystem';
import { isValidProjectId } from '../../utils/projectIds';
import { countWords } from '../../utils/markdown';
import AchievementToast from '../Rewards/AchievementToast';
import './AppLayout.css';

function AppLayout({ projectId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        sidebarCollapsed, aiPanelCollapsed,
        toggleSidebar, toggleAiPanel,
        focusMode, toggleFocusMode,
        aiPanelWidth, setAiPanelWidth
    } = useAppStore();
    const { activeTabPath, files, openFile, fileVersion, createNewFile } = useFileStore();
    const { bootstrapChats } = useAIStore();
    const { hasPendingChange } = usePendingChangesStore();

    const [showShortcuts, setShowShortcuts] = useState(false);
    const requestedPath = new URLSearchParams(location.search).get('path');
    const routedPathRef = useRef(null);

    // ── Mobile detection ──
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
    const [mobilePanel, setMobilePanel] = useState('editor'); // 'sidebar' | 'editor' | 'ai'

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // ── Resize handle logic ──
    const isResizing = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);

    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        isResizing.current = true;
        startX.current = e.clientX;
        startWidth.current = aiPanelWidth;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handleResizeMove = (ev) => {
            if (!isResizing.current) return;
            const delta = startX.current - ev.clientX;
            const newWidth = Math.min(600, Math.max(280, startWidth.current + delta));
            setAiPanelWidth(newWidth);
        };

        const handleResizeEnd = () => {
            isResizing.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', handleResizeEnd);
        };

        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', handleResizeEnd);
    }, [aiPanelWidth, setAiPanelWidth]);

    // ── Keyboard shortcuts ──
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Cmd+/ → toggle shortcuts overlay
            if ((e.metaKey || e.ctrlKey) && e.key === '/') {
                e.preventDefault();
                setShowShortcuts((prev) => !prev);
            }
            // Cmd+\ → toggle sidebar
            if ((e.metaKey || e.ctrlKey) && e.key === '\\' && !e.shiftKey) {
                e.preventDefault();
                toggleSidebar();
            }
            // Cmd+Shift+\ → toggle AI panel
            if ((e.metaKey || e.ctrlKey) && e.key === '\\' && e.shiftKey) {
                e.preventDefault();
                toggleAiPanel();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar, toggleAiPanel]);

    // Load AI messages on mount
    useEffect(() => {
        if (projectId) {
            bootstrapChats(projectId);
        }
    }, [projectId, bootstrapChats]);

    // Auto-open outline.md on first load
    useEffect(() => {
        if (files.length > 0 && !activeTabPath) {
            if (requestedPath) {
                return;
            }
            const outline = files.find(f => f.path === 'outline.md');
            if (outline) {
                openFile(outline.path);
            }
        }
    }, [files, activeTabPath, openFile, requestedPath]);

    useEffect(() => {
        if (!projectId || !requestedPath || files.length === 0) {
            return;
        }

        const routeKey = `${projectId}:${requestedPath}`;
        if (routedPathRef.current === routeKey) {
            return;
        }

        const target = files.find((file) => file.path === requestedPath);
        routedPathRef.current = routeKey;
        if (target) {
            openFile(target.path);
        }
    }, [files, openFile, projectId, requestedPath]);

    // Get current file
    const currentFile = files.find(f => f.path === activeTabPath);
    const isCanvas = currentFile && isCanvasFile(currentFile.path);
    const showDiff = activeTabPath && hasPendingChange(activeTabPath);

    // Dynamic grid columns
    const sidebarCol = sidebarCollapsed || focusMode ? '0px' : 'var(--sidebar-width)';
    const aiCol = aiPanelCollapsed || focusMode ? '0px' : `${aiPanelWidth}px`;

    // On mobile, when user opens a file from sidebar, switch to editor view
    const mobileOpenFile = useCallback((path) => {
        openFile(path);
        setMobilePanel('editor');
    }, [openFile]);

    // ── Mobile layout ──
    if (isMobile) {
        return (
            <div className="app-layout mobile-layout">
                <AchievementToast />

                {/* Mobile top bar */}
                <div className="mobile-header">
                    <div className="project-info">
                        <span className="project-icon">✒️</span>
                        <ProjectName />
                    </div>
                    <div className="mobile-header-actions">
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate(`/projects/${projectId}`)}
                        >
                            {t('projects.projectHome')}
                        </button>
                    </div>
                </div>

                {/* Mobile panel content */}
                <div className="mobile-content">
                    {mobilePanel === 'sidebar' && (
                        <aside className="sidebar mobile-panel-active">
                            <FileTree projectId={projectId} onMobileFileOpen={mobileOpenFile} />
                        </aside>
                    )}
                    {mobilePanel === 'editor' && (
                        <main className="main-content mobile-panel-active">
                            <EditorTabs projectId={projectId} />
                            <div className="editor-area">
                                {showDiff && (
                                    <DiffView projectId={projectId} filePath={activeTabPath} />
                                )}
                                {!showDiff && activeTabPath ? (
                                    isCanvas ? (
                                        <CanvasEditor projectId={projectId} path={activeTabPath} />
                                    ) : (
                                        <RichTextEditor key={`${activeTabPath}-${fileVersion}`} projectId={projectId} path={activeTabPath} />
                                    )
                                ) : !showDiff ? (
                                    <EmptyState projectId={projectId} />
                                ) : null}
                            </div>
                        </main>
                    )}
                    {mobilePanel === 'ai' && (
                        <aside className="ai-panel mobile-panel-active">
                            <AssistantPanel projectId={projectId} />
                        </aside>
                    )}
                </div>

                {/* Mobile bottom tab bar */}
                <div className="mobile-tab-bar">
                    <button
                        className={`mobile-tab ${mobilePanel === 'sidebar' ? 'active' : ''}`}
                        onClick={() => setMobilePanel('sidebar')}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                        </svg>
                        <span>{t('sidebar.files')}</span>
                    </button>
                    <button
                        className={`mobile-tab ${mobilePanel === 'editor' ? 'active' : ''}`}
                        onClick={() => setMobilePanel('editor')}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                        <span>{t('editor.write')}</span>
                    </button>
                    <button
                        className={`mobile-tab ${mobilePanel === 'ai' ? 'active' : ''}`}
                        onClick={() => setMobilePanel('ai')}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        <span>{t('ai.aiShort')}</span>
                    </button>
                </div>

                {showShortcuts && <KeyboardShortcuts onClose={() => setShowShortcuts(false)} />}
            </div>
        );
    }

    // ── Desktop layout ──
    return (
        <div
            className={`app-layout ${focusMode ? 'focus-mode' : ''}`}
            style={{ gridTemplateColumns: `${sidebarCol} 1fr auto ${aiCol}` }}
        >
            <AchievementToast />
            {/* Collapsed sidebar toggle */}
            {sidebarCollapsed && (
                <button
                    className="sidebar-toggle-collapsed"
                    onClick={toggleSidebar}
                    title={t('ai.showSidebar')}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="9" y1="3" x2="9" y2="21" />
                    </svg>
                </button>
            )}

            {/* Sidebar */}
            <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header">
                    <div className="project-info">
                        <span className="project-icon">✒️</span>
                        <ProjectName />
                    </div>
                    <div className="sidebar-actions">
                        {activeTabPath && (
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => navigate(`/projects/community?composer=1&projectId=${projectId}&sourcePath=${encodeURIComponent(activeTabPath)}`)}
                                title={t('ai.shareCommunity')}
                            >
                                {t('ai.shareExcerpt')}
                            </button>
                        )}
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate(`/projects/${projectId}`)}
                            title={t('projects.projectHome')}
                        >
                            {t('projects.projectHome')}
                        </button>
                        <button className="btn btn-icon btn-ghost" onClick={toggleSidebar} title={t('editor.toggleSidebar')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <line x1="9" y1="3" x2="9" y2="21" />
                            </svg>
                        </button>
                    </div>
                </div>
                <FileTree projectId={projectId} />
            </aside>

            {/* Main content area */}
            <main className="main-content">
                <EditorTabs projectId={projectId} />
                <div className="editor-area">
                    {/* Diff view overlay for pending AI changes */}
                    {showDiff && (
                        <DiffView projectId={projectId} filePath={activeTabPath} />
                    )}

                    {/* Regular editor view */}
                    {!showDiff && activeTabPath ? (
                        isCanvas ? (
                            <CanvasEditor projectId={projectId} path={activeTabPath} />
                        ) : (
                            <RichTextEditor key={`${activeTabPath}-${fileVersion}`} projectId={projectId} path={activeTabPath} />
                        )
                    ) : !showDiff ? (
                        <EmptyState projectId={projectId} />
                    ) : null}
                </div>
                <StatusBar
                    onToggleFocus={toggleFocusMode}
                    focusMode={focusMode}
                    onShowShortcuts={() => setShowShortcuts(true)}
                />
            </main>

            {/* Resize handle — only visible when AI panel is open */}
            {!aiPanelCollapsed && !focusMode && (
                <div
                    className="resize-handle"
                    onMouseDown={handleResizeStart}
                    title={t('common.dragToResize')}
                />
            )}

            {/* AI Panel */}
            <aside className={`ai-panel ${aiPanelCollapsed ? 'collapsed' : ''}`}>
                <div className="panel-header">
                    <span className="panel-title">{t('ai.aiAssistant')}</span>
                    <button className="btn btn-icon btn-ghost" onClick={toggleAiPanel} title={t('editor.toggleAiPanel')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <line x1="15" y1="3" x2="15" y2="21" />
                        </svg>
                    </button>
                </div>
                <AssistantPanel projectId={projectId} />
            </aside>

            {/* Collapsed AI panel toggle */}
            {aiPanelCollapsed && (
                <button
                    className="ai-toggle-collapsed"
                    onClick={toggleAiPanel}
                    title={t('ai.showAiAssistant')}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="15" y1="3" x2="15" y2="21" />
                    </svg>
                </button>
            )}

            {/* Keyboard shortcuts modal */}
            {showShortcuts && <KeyboardShortcuts onClose={() => setShowShortcuts(false)} />}
        </div>
    );
}

// Project name component (fetches from DB)
function ProjectName() {
    const { t } = useTranslation();
    const { currentProjectId } = useAppStore();
    const safeProjectId = isValidProjectId(currentProjectId) ? currentProjectId : null;
    const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : "skip");

    return (
        <span className="project-name">
            {project?.title || t('ai.untitledProject')}
        </span>
    );
}


// Improved empty state with quick action buttons
function EmptyState({ projectId }) {
    const { t } = useTranslation();
    const { createNewFile, openFile, files } = useFileStore();

    const handleNewChapter = async () => {
        const existingChapters = files.filter(f =>
            f.type === 'file' && f.parentPath === 'Chapters'
        );
        const num = existingChapters.length + 1;
        const name = `Chapter ${num}.md`;
        await createNewFile(projectId, 'Chapters', name, 'file');
    };

    const handleNewNote = async () => {
        const name = `Note - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.md`;
        await createNewFile(projectId, 'Notes', name, 'file');
    };

    const handleOpenOutline = () => {
        const outline = files.find(f => f.path === 'outline.md');
        if (outline) {
            openFile(outline.path);
        }
    };

    const hasOutline = files.some(f => f.path === 'outline.md');

    return (
        <div className="empty-state">
            <div className="empty-content">
                <span className="empty-icon">📝</span>
                <h3>{t('editor.emptyState.readyToWrite')}</h3>
                <p>{t('editor.emptyState.selectFile')}</p>
                <div className="quick-actions">
                    <button className="quick-action-card" onClick={handleNewChapter}>
                        <span className="qa-icon">📖</span>
                        <span className="qa-label">{t('editor.emptyState.newChapter')}</span>
                    </button>
                    <button className="quick-action-card" onClick={handleNewNote}>
                        <span className="qa-icon">🗒️</span>
                        <span className="qa-label">{t('editor.emptyState.newNote')}</span>
                    </button>
                    {hasOutline && (
                        <button className="quick-action-card" onClick={handleOpenOutline}>
                            <span className="qa-icon">🗺️</span>
                            <span className="qa-label">{t('editor.emptyState.openOutline')}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// Enhanced status bar
function StatusBar({ onToggleFocus, focusMode, onShowShortcuts }) {
    const { t } = useTranslation();
    const { activeTabPath, getFileContent } = useFileStore();
    const { dailyGoal, setDailyGoal, wordsWrittenToday, theme, setTheme } = useAppStore();
    const writingInsights = useQuery(api.projects.getWritingInsights, { days: 7 });
    const [editingGoal, setEditingGoal] = useState(false);
    const [goalInput, setGoalInput] = useState(String(dailyGoal));
    const goalInputRef = useRef(null);

    const content = activeTabPath ? getFileContent(activeTabPath) : '';
    const wordCount = countWords(content);
    const charCount = content.length;
    const paragraphCount = content ? content.split(/\n\s*\n/).filter(p => p.trim().length > 0).length : 0;
    const readingTime = Math.max(1, Math.ceil(wordCount / 200));

    // Use DB-backed todayWords, fall back to session counter
    const todayWords = writingInsights?.todayWords ?? wordsWrittenToday;
    const goalProgress = Math.min((todayWords / dailyGoal) * 100, 100);

    useEffect(() => {
        if (editingGoal && goalInputRef.current) {
            goalInputRef.current.focus();
            goalInputRef.current.select();
        }
    }, [editingGoal]);

    const handleGoalSubmit = () => {
        const val = parseInt(goalInput, 10);
        if (val > 0) setDailyGoal(val);
        setEditingGoal(false);
    };

    return (
        <div className="status-bar">
            <div className="status-left">
                {activeTabPath && (
                    <>
                        <span className="status-item">{t('editor.statusBar.wordsCount', { count: wordCount })}</span>
                        <span className="status-separator">•</span>
                        <span className="status-item">{t('editor.statusBar.charsCount', { count: charCount })}</span>
                        <span className="status-separator">•</span>
                        <span className="status-item">{t('editor.statusBar.paragraphCount', { count: paragraphCount })}</span>
                        <span className="status-separator">•</span>
                        <span className="status-item">{t('editor.statusBar.readingTime', { count: readingTime })}</span>
                    </>
                )}
            </div>
            <div className="status-right">
                <div className="daily-goal" title={t('editor.statusBar.goalProgressTitle', { current: todayWords, goal: dailyGoal })}>
                    <span className="goal-label">{t('editor.statusBar.goal')}</span>
                    <div className="goal-bar">
                        <div className="goal-progress" style={{ width: `${goalProgress}%` }}></div>
                    </div>
                    {editingGoal ? (
                        <input
                            ref={goalInputRef}
                            className="goal-input"
                            type="number"
                            value={goalInput}
                            onChange={(e) => setGoalInput(e.target.value)}
                            onBlur={handleGoalSubmit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleGoalSubmit();
                                if (e.key === 'Escape') setEditingGoal(false);
                            }}
                        />
                    ) : (
                        <span
                            className="goal-count clickable"
                            onClick={() => {
                                setGoalInput(String(dailyGoal));
                                setEditingGoal(true);
                            }}
                            title={t('editor.statusBar.goal')}
                        >
                            {todayWords}/{dailyGoal}
                        </span>
                    )}
                </div>

                <button
                    className={`status-btn ${focusMode ? 'active' : ''}`}
                    onClick={onToggleFocus}
                    title={t('editor.statusBar.focusMode')}
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                    </svg>
                </button>

                <button
                    className={`status-btn ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    title={theme === 'dark' ? t('editor.statusBar.lightMode', 'Light mode') : t('editor.statusBar.darkMode', 'Dark mode')}
                >
                    {theme === 'dark' ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="5" />
                            <line x1="12" y1="1" x2="12" y2="3" />
                            <line x1="12" y1="21" x2="12" y2="23" />
                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                            <line x1="1" y1="12" x2="3" y2="12" />
                            <line x1="21" y1="12" x2="23" y2="12" />
                            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                        </svg>
                    ) : (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                        </svg>
                    )}
                </button>

                <button
                    className="status-btn"
                    onClick={onShowShortcuts}
                    title={t('editor.statusBar.keyboardShortcuts')}
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <line x1="6" y1="8" x2="6" y2="8" />
                        <line x1="10" y1="8" x2="10" y2="8" />
                        <line x1="14" y1="8" x2="14" y2="8" />
                        <line x1="18" y1="8" x2="18" y2="8" />
                        <line x1="6" y1="12" x2="18" y2="12" />
                        <line x1="6" y1="16" x2="6" y2="16" />
                        <line x1="18" y1="16" x2="18" y2="16" />
                        <line x1="10" y1="16" x2="14" y2="16" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

export default AppLayout;
